import { useMemo, useSyncExternalStore } from 'react';

import { IncidentTypes } from './IncidentTypes';

/**
 * Categories typed with + stay in the picker until the page is reloaded. Saved
 * incidents already persist the string on `incident_types`.
 */
let sessionCategories: string[] = [];
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

export function getSessionIncidentCategories(): string[] {
  return sessionCategories;
}

export function subscribeSessionIncidentCategories(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Returns the existing spelling when the text is already a category. */
export function addSessionIncidentCategory(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  const known = [...IncidentTypes, ...sessionCategories];
  const match = known.find(
    (item) => item.toLowerCase() === trimmed.toLowerCase()
  );
  if (match) return match;
  sessionCategories = [...sessionCategories, trimmed];
  emit();
  return trimmed;
}

export function useSessionIncidentCategories(): string[] {
  return useSyncExternalStore(
    subscribeSessionIncidentCategories,
    getSessionIncidentCategories,
    getSessionIncidentCategories
  );
}

/**
 * Built-in categories, plus session additions and values already on an
 * incident.
 */
export function useIncidentCategoryOptions(selected: string[] = []) {
  const session = useSessionIncidentCategories();
  return useMemo(() => {
    const seen = new Set<string>();
    const values: string[] = [];
    for (const value of [...session, ...selected, ...IncidentTypes]) {
      const trimmed = value.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      values.push(trimmed);
    }
    return values.map((value) => ({ value, label: value }));
  }, [session, selected]);
}
